/**
 * U.S. USCIS A-Number (Alien Registration Number) validation.
 * Keep in sync with Portal-Backend/services/usANumberValidation.js
 */

export function extractANumberDigits(raw: unknown): string {
  return String(raw ?? "")
    .replace(/^A[-\s]*/i, "")
    .replace(/\D/g, "");
}

/** Display with optional A- prefix (up to 9 digits while typing). */
export function formatANumberDisplay(digits: string): string {
  const d = digits.slice(0, 9);
  return d.length ? `A-${d}` : "";
}

export type UsANumberValidationResult =
  | { ok: true; normalized: string }
  | { ok: false; message: string };

export function validateUsANumber(raw: unknown): UsANumberValidationResult {
  const digits = extractANumberDigits(raw);

  // USCIS forms print the A-Number in nine boxes, and tell applicants with a
  // shorter number to add zeros at the start. Seven and eight digits used to
  // be accepted and padded silently, so a number missing a digit — a typo —
  // was indistinguishable from a genuine short one and went through.
  if (digits.length !== 9) {
    return {
      ok: false,
      message: "Enter all 9 digits of the A-Number. If it has fewer than 9, add zeros at the start (for example, 012345678).",
    };
  }

  if (/^0+$/.test(digits)) {
    return { ok: false, message: "Enter a valid A-Number." };
  }

  const normalized = digits;
  return { ok: true, normalized };
}
